import { ISharedOkrsAccountSetting } from "@gtmhub/accounts/accountsService";
import { IDirective, IScope, IDirectiveFactory, IAngularEvent, noop } from "angular";
import { IModalService } from "angular-ui-bootstrap";
import { StateService } from "@uirouter/angularjs";
import { localize } from "@gtmhub/localization";
import { IInsight, IInsightFilters, ICreateKrKpiFilter } from "@gtmhub/insights";
import { IKpiFormData } from "@gtmhub/kpis/models/models";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { GoalModalsService } from "@gtmhub/goals/goalModalsService";
import { IGoal } from "@gtmhub/goals/models";
import { IMetric, IMetricLinks } from "@gtmhub/okrs/metrics";

interface IMetricParentScope extends IScope {
  data: any;
  insightDetails?: {
    insight: IInsight;
  };
  insight?: IInsight;
}

interface IMetricScope extends IScope {
  createKpi(): void;
  positionDropdown(): void;
  hasManageDataPermission(): boolean;
  name: any;
  selector: string;
  addToGoal(): void;
  selectedFilters: { [name: string]: string };
  creationAllowed: boolean;
  tooltip: string;
  fieldName: string;
  $root: IGtmhubRootScopeService;
  insightboardMode: "view" | "edit";
  sharedOkrsAccountSetting: ISharedOkrsAccountSetting;
}

const metricFactory = (
  initialValue: number,
  goal: IGoal,
  insight: IInsight,
  scope: IMetricScope,
  fieldName: string,
  generateFilters: () => ICreateKrKpiFilter[]
): IMetric => ({
  goalId: goal.id,
  insightName: insight.name,
  fieldName: fieldName,
  name: scope.name ? scope.name : `${insight.title} ${fieldName}`,
  initialValue,
  actual: initialValue,
  target: initialValue * 1.5,
  // setting critical to null when creating a new metric
  critical: null,
  targetOperator: "at_least",
  settings: generateFilters(),
  ownerIds: goal.ownerIds,
  manualType: "",
  dueDate: null,
  softDueDate: null,
  links: <IMetricLinks>{},
  customFields: {},
  format: {
    prefix: "",
    suffix: "",
    fractionSize: 0,
  },
});

export class Metric implements IDirective {
  public scope = {
    name: "@",
    selector: "@",
    fieldName: "@",
    selectedFilters: "=",
  };
  public restrict = "E";
  public transclude = true;
  public templateUrl = require("../views/metric.jade");

  constructor(private $state: StateService, private $uibModal: IModalService, private goalModalsService: GoalModalsService) {}

  public link(scope: IMetricScope): void {
    scope.$on("insightboardModeChanged", (event: IAngularEvent, insightboardMode: "view" | "edit") => {
      scope.insightboardMode = insightboardMode;
    });

    let insight: IInsight, fieldName: string, parentData: any;

    const setTooltip = (data: Record<string, unknown>, insightDataFieldName: string): string => {
      if (!insightDataFieldName) {
        return localize("metric_must_have_field_name_defined");
      }

      if (typeof data[insightDataFieldName] === "undefined" || data[insightDataFieldName] === null || data[insightDataFieldName] === undefined) {
        return `${localize("insight_data_doesnt_have_field_name")} "${insightDataFieldName}".`;
      }

      if (typeof data[insightDataFieldName] !== "number") {
        return localize("value_not_stored_as_number");
      }

      return localize("add_this_number_as_target");
    };

    const isCreationAllowed = (data: any, insightDataFieldName: string): boolean => {
      return insightDataFieldName && (data[insightDataFieldName] || data[insightDataFieldName] === 0) && typeof data[insightDataFieldName] === "number";
    };

    const init = (): void => {
      const parentScope = scope.$parent as IMetricParentScope;
      insight = parentScope.insight ? parentScope.insight : parentScope.insightDetails && parentScope.insightDetails.insight ? parentScope.insightDetails.insight : null;

      fieldName = scope.fieldName;
      parentData = parentScope.data;

      const randomizedId = Math.floor(Math.random() * 40);
      scope.selector = "dropdown-" + randomizedId + insight.id;
      scope.tooltip = setTooltip(parentData, fieldName);
      scope.creationAllowed = isCreationAllowed(parentData, fieldName);
    };

    // second condition is for current metrics that do not have the selected-filters attribute set
    const generateFilters = (selectedFilters: IInsightFilters = scope.selectedFilters || scope.$parent.$parent["selectedFilters"]): ICreateKrKpiFilter[] => {
      const filters: ICreateKrKpiFilter[] = [];

      for (const filterName in selectedFilters) {
        if (selectedFilters[filterName] !== null) {
          filters.push({
            name: filterName,
            value: selectedFilters[filterName],
          });
        }
      }

      return filters;
    };

    const checkSimilarity = (newMetric: IMetric, oldMetric: IMetric): boolean => {
      let similar = false;
      if (oldMetric.settings === null && newMetric.settings.length === 0) {
        newMetric.settings = null;
      }

      if (
        newMetric.insightName === oldMetric.insightName &&
        newMetric.fieldName === oldMetric.fieldName &&
        JSON.stringify(newMetric.settings) === JSON.stringify(oldMetric.settings)
      ) {
        similar = true;
      }

      return similar;
    };

    const createDynamicMetric = (metric: IMetric, goal: IGoal) => {
      this.$state.go("._goalSidebar.goal._createMetric.createDynamicMetric", {
        id: goal.id,
        metric,
      });
    };

    const onGoalSelected = (goal: IGoal) => {
      if (!goal.locked) {
        const initialValue = parentData[fieldName];

        const metric: IMetric = metricFactory(initialValue, goal, insight, scope, fieldName, generateFilters);

        if (goal.metrics) {
          let isDuplicate = false;
          for (let i = 0; i < goal.metrics.length; i++) {
            if (checkSimilarity(metric, goal.metrics[i])) {
              isDuplicate = true;
            }
          }

          if (!isDuplicate) {
            createDynamicMetric(metric, goal);
          }
        } else {
          createDynamicMetric(metric, goal);
        }
      } else {
        openErrorModal();
      }
    };

    const openErrorModal = () => {
      this.$uibModal.open({
        templateUrl: require("../../okrs/metrics/views/cannot-add-dynamic-metric.jade"),
        windowClass: "medium",
        controller: [
          "$scope",
          "$uibModalInstance",
          function ($scope, $uibModalInstance) {
            $scope.confirm = () => {
              $uibModalInstance.close();
            };

            $scope.close = () => {
              $uibModalInstance.close();
            };
          },
        ],
      });
    };

    scope.addToGoal = (): void => {
      if (!scope.creationAllowed) {
        return;
      }

      this.goalModalsService.openGoalSelectModal({ invokedBy: "KR" }).then((result) => {
        if (result.type === "goal") {
          onGoalSelected(result.goal);
        }
      });
    };

    const generateKpiFormData = (): IKpiFormData => {
      return {
        insightLink: {
          insight: insight,
          fieldName: fieldName,
          initialValue: parentData[fieldName],
          filters: generateFilters(),
        },
      };
    };

    scope.hasManageDataPermission = (): boolean => {
      return scope.$root.hasPermission("ManageData");
    };

    scope.createKpi = (): void => {
      if (!scope.hasManageDataPermission() || !scope.creationAllowed) {
        return;
      }

      this.$uibModal
        .open({
          templateUrl: require("../../kpis/views/form.jade"),
          windowClass: "medium-large kpi-modal",
          backdropClass: "dark-backdrop",
          backdrop: false,
          resolve: {
            formData: generateKpiFormData,
          },
          controller: "KpiFormCtrl",
        })
        .result.catch(noop);
    };

    const positionDropdown = () => {
      const dropdown = document.querySelector<HTMLElement>("." + scope.selector);
      const button = dropdown.previousElementSibling;
      const left = button.getBoundingClientRect().left;
      const top = button.getBoundingClientRect().top + button.clientHeight;
      dropdown.style.top = top + "px";
      dropdown.style.left = left + "px";
    };

    scope.positionDropdown = positionDropdown;

    window.addEventListener("resize", positionDropdown);

    scope.$on("$destroy", function () {
      window.removeEventListener("resize", positionDropdown);
    });

    init();
  }

  public static factory(): IDirectiveFactory {
    const directive = ($state: StateService, $uibModal: IModalService, goalModalsService: GoalModalsService) => new Metric($state, $uibModal, goalModalsService);

    directive.$inject = ["$state", "$uibModal", "GoalModalsService"];

    return directive;
  }
}
